import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

// Material UI
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import { alpha, useTheme } from "@mui/material/styles";

// Icons
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import QrCodeIcon from "@mui/icons-material/QrCode";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RefreshIcon from "@mui/icons-material/Refresh";

// Components
import {
  AnimatedModal,
  ModalContent,
  ModalHeader,
} from "../../modals/AnimatedModal";

// Services
import {
  createWhatsAppInstance,
  getWhatsAppQRCode,
  getWhatsAppStatus,
} from "../../../api/DeliveryCenter/whatsapp";
import { dispatchSnackBarMessage } from "../../../utils/snackbar";
import { extractApiErrorMessage } from "../../../utils/subscriptionErrors";

const POLL_INTERVAL = 3000;
const QR_TIMEOUT = 60000;
const QR_RETRY_INTERVAL = 4000;
const QR_MAX_RETRIES = 5;

export default function CreateWhatsAppInstanceModal({
  show,
  closeModal,
  onCreated,
}) {
  const theme = useTheme();

  // Step 1: Name
  const [instanceName, setInstanceName] = useState("");
  const [nameError, setNameError] = useState("");
  const [creating, setCreating] = useState(false);

  // Step 2: QR Code
  const [step, setStep] = useState(1);
  const [instanceId, setInstanceId] = useState(null);
  const [qrCodeBase64, setQrCodeBase64] = useState("");
  const [connected, setConnected] = useState(false);
  const [qrExpired, setQrExpired] = useState(false);
  const [qrFailed, setQrFailed] = useState(false);
  const [refreshingQr, setRefreshingQr] = useState(false);

  const pollRef = useRef(null);
  const qrTimerRef = useRef(null);
  const retryRef = useRef(null);
  const retryCountRef = useRef(0);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearInterval(pollRef.current);
      clearTimeout(qrTimerRef.current);
      clearTimeout(retryRef.current);
    };
  }, []);

  const startPolling = useCallback(
    (id) => {
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await getWhatsAppStatus(id);
          if (data.status === "connected") {
            clearInterval(pollRef.current);
            clearTimeout(qrTimerRef.current);
            setConnected(true);

            dispatchSnackBarMessage(
              `whatsapp-connected-${new Date()}`,
              "WhatsApp conectado com sucesso!",
              "success"
            );

            setTimeout(() => {
              onCreated();
            }, 2000);
          }
        } catch (e) {
          // Silently ignore polling errors
        }
      }, POLL_INTERVAL);
    },
    [onCreated]
  );

  const startQrTimer = useCallback(() => {
    clearTimeout(qrTimerRef.current);
    setQrExpired(false);
    qrTimerRef.current = setTimeout(() => {
      setQrExpired(true);
      clearInterval(pollRef.current);
    }, QR_TIMEOUT);
  }, []);

  const retryQrCode = useCallback(
    (id) => {
      retryRef.current = setTimeout(async () => {
        try {
          const { data } = await getWhatsAppQRCode(id);
          if (data.qr_code_base64) {
            retryCountRef.current = 0;
            setQrFailed(false);
            setQrCodeBase64(data.qr_code_base64);
            startQrTimer();
            startPolling(id);
            return;
          }
        } catch (_) {}
        if (retryCountRef.current < QR_MAX_RETRIES) {
          retryCountRef.current += 1;
          retryQrCode(id);
        } else {
          setQrFailed(true);
        }
      }, QR_RETRY_INTERVAL);
    },
    [startQrTimer, startPolling]
  );

  const handleCreate = useCallback(async () => {
    const nameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!instanceName.trim()) {
      setNameError("Nome da instância é obrigatório.");
      return;
    }
    if (!nameRegex.test(instanceName)) {
      setNameError("Use apenas letras, números, hifens e underscores.");
      return;
    }

    setNameError("");
    setCreating(true);

    try {
      const { data } = await createWhatsAppInstance({
        instance_name: instanceName,
      });

      setInstanceId(data.id);
      setStep(2);
      setQrFailed(false);

      if (data.qr_code_base64) {
        setQrCodeBase64(data.qr_code_base64);
        startQrTimer();
        startPolling(data.id);
      } else {
        retryCountRef.current = 0;
        retryQrCode(data.id);
      }

      dispatchSnackBarMessage(
        `whatsapp-created-${new Date()}`,
        "Instância criada. Escaneie o QR Code.",
        "info"
      );
    } catch (e) {
      const message =
        extractApiErrorMessage(e, "") ||
        e.response?.data?.instance_name?.[0] ||
        "Erro ao criar instância.";
      dispatchSnackBarMessage(
        `whatsapp-error-${new Date()}`,
        message,
        "error"
      );
    } finally {
      setCreating(false);
    }
  }, [instanceName, startPolling, startQrTimer, retryQrCode]);

  const handleRefreshQr = useCallback(async () => {
    if (!instanceId) return;
    setRefreshingQr(true);
    setQrFailed(false);
    setQrExpired(false);
    try {
      const { data } = await getWhatsAppQRCode(instanceId);
      if (data.qr_code_base64) {
        setQrCodeBase64(data.qr_code_base64);
        startQrTimer();
        startPolling(instanceId);
      } else {
        retryCountRef.current = 0;
        retryQrCode(instanceId);
      }
    } catch (e) {
      dispatchSnackBarMessage(
        `whatsapp-error-${new Date()}`,
        "Erro ao atualizar QR Code.",
        "error"
      );
    } finally {
      setRefreshingQr(false);
    }
  }, [instanceId, startQrTimer, startPolling, retryQrCode]);

  const handleClose = useCallback(() => {
    clearInterval(pollRef.current);
    clearTimeout(qrTimerRef.current);
    clearTimeout(retryRef.current);
    retryCountRef.current = 0;
    setStep(1);
    setInstanceName("");
    setInstanceId(null);
    setQrCodeBase64("");
    setConnected(false);
    setQrExpired(false);
    setQrFailed(false);
    setNameError("");
    closeModal();
  }, [closeModal]);

  return (
    <AnimatedModal
      isOpen={show}
      onClose={handleClose}
      width="90%"
      maxWidth="480px"
      zIndex={10000}
    >
      <ModalHeader
        title={step === 1 ? "Nova Instância WhatsApp" : "Conectar WhatsApp"}
        icon={step === 1 ? WhatsAppIcon : QrCodeIcon}
        onClose={handleClose}
      />
      <ModalContent>
        <Box sx={{ px: 3, py: 2 }}>
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3 }}
              >
                Escolha um nome para identificar sua instância WhatsApp.
              </Typography>

              <TextField
                fullWidth
                label="Nome da Instância"
                placeholder="ex: minha-loja"
                value={instanceName}
                onChange={(e) => {
                  setInstanceName(e.target.value);
                  setNameError("");
                }}
                error={!!nameError}
                helperText={
                  nameError || "Use letras, números, hifens e underscores"
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreate();
                }}
                sx={{ mb: 3 }}
              />

              <Button
                fullWidth
                variant="contained"
                onClick={handleCreate}
                disabled={creating}
                startIcon={
                  creating ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <WhatsAppIcon />
                  )
                }
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 500,
                  py: 1.5,
                  backgroundColor: "#25D366",
                  "&:hover": { backgroundColor: "#1da851" },
                }}
              >
                {creating ? "Criando..." : "Criar Instância"}
              </Button>
            </motion.div>
          )}

          {step === 2 && !connected && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  sx={{ mb: 3 }}
                >
                  Abra o WhatsApp no celular, acesse{" "}
                  <strong>Dispositivos Conectados</strong> e escaneie o QR Code
                  abaixo.
                </Typography>

                {qrExpired ? (
                  <Box
                    sx={{
                      width: 280,
                      height: 280,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      border: `2px dashed ${alpha(
                        theme.palette.warning.main,
                        0.5
                      )}`,
                      borderRadius: 3,
                      mb: 2,
                    }}
                  >
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                      QR Code expirado
                    </Alert>
                    <Button
                      variant="outlined"
                      startIcon={
                        refreshingQr ? (
                          <CircularProgress size={18} />
                        ) : (
                          <RefreshIcon />
                        )
                      }
                      onClick={handleRefreshQr}
                      disabled={refreshingQr}
                      sx={{ borderRadius: 2, textTransform: "none" }}
                    >
                      Atualizar QR Code
                    </Button>
                  </Box>
                ) : qrCodeBase64 ? (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      backgroundColor: "#fff",
                      mb: 2,
                    }}
                  >
                    <img
                      src={
                        qrCodeBase64.startsWith("data:")
                          ? qrCodeBase64
                          : `data:image/png;base64,${qrCodeBase64}`
                      }
                      alt="QR Code WhatsApp"
                      style={{ width: 260, height: 260 }}
                    />
                  </Box>
                ) : qrFailed ? (
                  <Box
                    sx={{
                      width: 280,
                      height: 280,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      border: `2px dashed ${alpha(
                        theme.palette.error.main,
                        0.4
                      )}`,
                      borderRadius: 3,
                      mb: 2,
                    }}
                  >
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                      Nao foi possivel obter o QR Code
                    </Alert>
                    <Button
                      variant="outlined"
                      startIcon={
                        refreshingQr ? (
                          <CircularProgress size={18} />
                        ) : (
                          <RefreshIcon />
                        )
                      }
                      onClick={handleRefreshQr}
                      disabled={refreshingQr}
                      sx={{ borderRadius: 2, textTransform: "none" }}
                    >
                      Tentar novamente
                    </Button>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: 280,
                      height: 280,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress sx={{ color: "#25D366", mb: 2 }} />
                    <Typography variant="caption" color="text.secondary">
                      Obtendo QR Code...
                    </Typography>
                  </Box>
                )}

                <Typography
                  variant="caption"
                  color="text.secondary"
                  align="center"
                >
                  Aguardando conexão...
                </Typography>
              </Box>
            </motion.div>
          )}

          {step === 2 && connected && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  py: 4,
                }}
              >
                <CheckCircleIcon
                  sx={{ fontSize: 72, color: "#25D366", mb: 2 }}
                />
                <Typography
                  variant="h6"
                  fontWeight={600}
                  align="center"
                  gutterBottom
                >
                  WhatsApp Conectado!
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  Sua instância foi conectada com sucesso.
                </Typography>
              </Box>
            </motion.div>
          )}
        </Box>
      </ModalContent>
    </AnimatedModal>
  );
}
